import { ASTExpr } from '../lscript/ast';
import { UnknownUnitError } from './UnknownUnitError';
import { parseUnitString } from './parse';
import { AtomicUnit, Unit, Conversion, UnitSpec } from './types';
import memoize from 'lodash/memoize';
import invariant from 'invariant';
import must from '../utils/must';
import { l } from '../lscript/template';
import { expandUnit } from './operations';
import isNullish from '@watershed/shared-util/isNullish';

/*
 * A registry of units and their conversions.  The registry is a map from
 * a unit to a RegistryEntry.  The RegistryEntry contains the canonical
 * name of the unit, and a map from other units to conversions.
 */
interface RegistryEntry {
  canonicalName: AtomicUnit;
  isBaseUnit: boolean;
  isCurrency: boolean;
  conversions: Map<Unit, Conversion>;
}

const registry: Map<string, RegistryEntry> = new Map();
const names: Map<AtomicUnit, Unit> = new Map();
const unitAliases: Map<Unit, Array<AtomicUnit>> = new Map();

function registryGet(u: Unit): RegistryEntry | undefined {
  return registry.get(inspectUnit(u));
}

function registrySet(u: Unit, entry: RegistryEntry): void {
  registry.set(inspectUnit(u), entry);
}

export function isBaseUnit(u: Unit): boolean {
  const entry = registryGet(u);
  return entry ? entry.isBaseUnit : false;
}

/*
 * Find a predefined conversion between two units.  If no conversion is
 * found, return undefined.
 */
export function registryGetConversion(
  from: Unit,
  to: Unit
): Conversion | undefined {
  const entry = registryGet(from);
  if (!entry) {
    return undefined;
  }
  const conversion = entry.conversions.get(to);
  if (!conversion) {
    return undefined;
  }
  return conversion;
}

/*
 * An empty unit (scalar).  This may result if a unit multiplication
 * or division cancels out.
 */
export const NullUnit: Unit = new Map();

/*
 * Lookup a unit by name.  If the name is not found, throw an error.
 */
export function getUnitBang(name: string): Unit {
  const atomicUnit = getAtomicUnitBang(name);
  const unit = names.get(atomicUnit);
  if (!unit) {
    throw new UnknownUnitError(name);
  }
  return unit;
}

/*
 * Look up a unit by name.  If the name is not found, return undefined.
 */
export function getUnit(name: string): Unit | undefined {
  return names.get(name as AtomicUnit);
}

/*
 * Convert a string to an AtomicUnit if it is a valid unit name.
 */
export function getAtomicUnit(name: string): AtomicUnit | undefined {
  return names.has(name as AtomicUnit) ? (name as AtomicUnit) : undefined;
}

/*
 * Convert a string to an AtomicUnit if it is a valid unit name.
 */
export function getAtomicUnitBang(name: string): AtomicUnit {
  if (!names.has(name as AtomicUnit)) {
    throw new UnknownUnitError(name);
  }
  return name as AtomicUnit;
}

/*
 * Get currency unit
 */
export function getCurrencyUnit(name: string): Unit | undefined {
  const unit = getUnit(name);
  if (!unit) {
    return undefined;
  }
  const entry = registryGet(unit);
  if (!entry) {
    return undefined;
  }
  return entry.isCurrency ? unit : undefined;
}

/*
 * Return all known base units for a given unit.
 */
export function baseUnitsFor(unit: Unit): Array<Unit> {
  const entry = registryGet(unit);
  if (!entry) {
    return [];
  }
  return [...entry.conversions.keys()];
}

/*
 * Find the all base units that are common to both units.
 */
export function findBaseUnits(fromUnit: Unit, toUnit: Unit): Array<Unit> {
  const fromKeys = new Set(baseUnitsFor(fromUnit));
  const toKeys = baseUnitsFor(toUnit);
  const intersection = new Set(toKeys.filter((x) => fromKeys.has(x)));

  return [...intersection];
}

/*
 * Define a new unit.  This is the hard way; you probably want to use
 * one of the helper functions below.
 */
export function defUnit(
  canonicalName: AtomicUnit,
  unit: Unit,
  isBaseUnit: boolean,
  isCurrency: boolean,
  conversions: Map<Unit, Conversion>
): Unit {
  invariant(
    registryGet(unit) === undefined,
    `Unit ${canonicalName} already defined`
  );

  registrySet(unit, { canonicalName, isBaseUnit, conversions, isCurrency });

  // TODO someday maybe -- make a way to define multiple unit structures for the
  // same canonical name.  For example, we might want to define "joule" as both
  // "newton-meter" and "kilogram-meter^2/second^2".
  if (names.has(canonicalName)) {
    throw new Error(`Unit name ${canonicalName} already defined`);
  }

  names.set(canonicalName, unit);
  return unit;
}

/*
 * Register another name that a unit can be referred to by. This is applied
 * only during parsing and lookup -- the canonical name will always be used
 * as the key in unit maps.
 */
export function defAlias(alias: string, unit: string): Unit {
  if (names.has(alias as AtomicUnit)) {
    throw new Error(`Unit name ${alias} already defined`);
  }
  const canonicalUnit = getUnitBang(unit);
  names.set(alias as AtomicUnit, canonicalUnit);
  const units = unitAliases.get(canonicalUnit) ?? [];
  units.push(alias as AtomicUnit);
  unitAliases.set(canonicalUnit, units);
  return canonicalUnit;
}

/*
 * Define a base unit.  A base unit is a unit that is not defined in terms of any
 * other unit.  For example, "meter" is a base unit, but "mile" is not.
 *
 * This does not map directly to the SI notion of base units, though it includes them.
 * In our conception, anything you want can be a base unit; it's intended to be used
 * as the default unit for a dimension.
 *
 * Examples:
 *   defBaseUnit("meter")
 *   defBaseUnit("joule", "newton-meter")
 */
export function defBaseUnit(name: string, unitSpec?: UnitSpec): Unit {
  let unit: Unit;
  if (unitSpec) {
    unit = expandUnit(normalizeUnit(makeUnit(unitSpec)));
  } else {
    unit = makeUnit({ [name]: 1 });
  }

  const identity = (v: ASTExpr) => v;
  return defUnit(
    name as AtomicUnit,
    unit,
    true,
    false,
    new Map([[unit, { fromBase: identity, toBase: identity }]])
  );
}

/*
 * Define a currency Unit.  This is a special kind of base unit that is used
 * for currency conversions.  They can interact with other units as usual, but
 * they are not convertible to other units inside the unit system. If you don't
 * define a currency unit using this function, it won't be recognized as a currency.
 */
export function defCurrencyUnit(name: string): Unit {
  const unit = normalizeUnit(makeUnit({ [name]: 1 }), [name as AtomicUnit]);
  const identity = (v: ASTExpr) => v;
  return defUnit(
    name as AtomicUnit,
    unit,
    true,
    true,
    new Map([[unit, { fromBase: identity, toBase: identity }]])
  );
}

/*
 * Define a new unit in terms of a constant scaling factor to/from a canonical
 * unit.  When the canonical unit is the same as the unit, this defines the
 * canonical unit, and the conversion is a no-op.
 *
 * Simple units can be definied in terms of other simple units, but internally
 * they are always defined in terms of a real base unit.  For example, "mile" can
 * be defined in terms of "foot", but internally it is defined in terms of "meter",
 * which is the base unit for length.
 *
 * Example:
 *    defSimpleUnit('kilomter', 'meter', 1000) -> (1000 m)
 *    defSimpleUnit("mile", "foot", 5280) -> (1609.344 m)
 */
export function defSimpleUnit(
  name: string,
  baseName: string,
  factor: number
): Unit {
  const unit = normalizeUnit(makeUnit({ [name]: 1 }), [name as AtomicUnit]);
  const baseUnit = name === baseName ? unit : getUnitBang(baseName);
  const conversion = maybeComposeConversions(unit, baseUnit, {
    toBase: (v) => l`${v} * ${factor}`,
    fromBase: (v) => l`${v} / ${factor}`,
  });
  return defUnit(name as AtomicUnit, unit, false, false, new Map([conversion]));
}

/*
 * Define a new unit which relates to its base unit by an inverted conversion.
 *
 * The way inverted relationships work is x * y = Cf where Cf is the conversion factor.
 * So x = Cf / and y = Cf / x.  This is useful for defining units like "denier" and "miles/gallon",
 * which are defined in terms of a constant factor to the base, but with the relationship reversed.
 *
 * Example:
 *   defInvertedUnit("denier", "gram/9000m", "number_metric", 9000)
 *   defInvertedUnit("mpg", "mile/gallon", "liter/100km", 235.215)
 */
export function defInvertedUnit(
  name: string,
  unitSpec: UnitSpec,
  baseName: string,
  factor: number
): Unit {
  const unit = normalizeUnit(makeUnit(unitSpec), [name as AtomicUnit]);
  const baseUnit = name === baseName ? unit : getUnitBang(baseName);

  const entry = registryGet(baseUnit);
  if (entry && !entry.isBaseUnit) {
    throw new Error(
      `Base unit ${baseName} for ${name} is not a base unit, but a derived unit. ` +
        'defInvertedUnit only works with base units.'
    );
  }

  return defUnit(
    name as AtomicUnit,
    unit,
    false,
    false,
    new Map([
      [
        baseUnit,
        {
          toBase: (v) => l`${factor} / ${v}`,
          fromBase: (v) => l`${factor} / ${v}`,
        },
      ],
    ])
  );
}

/*
 * Define a new unit in terms of an explicit conversion formula to/from a canonical unit. Can be used to define
 * units that are canonically mixed units, such as "mile/gallon" or "kilowatt-hour", in which case you can
 * specify the a simple conversion if appropriate.
 *
 * Note: The special variable 'v' is used in formulas to represent the value being converted.
 *
 * Example:
 *  defCustomUnit("l/100km", {km: 1, "100km": -1}, "l/100km", { toBase: "v", fromBase: "v" })
 *  defCustomUnit("mpg", "mile/gallon", "l/100km", { toBase: "v * 235.215", fromBase: "v / 235.215"})
 *
 *  defCustomUnit('kwh', 'kilowatt-hour', 'joule', 3600000)
 */
export function defCustomUnit(
  name: string,
  unitSpec: UnitSpec,
  baseName: string,
  conversion: Conversion | number
): Unit {
  if (typeof conversion === 'number') {
    const c = conversion; // Necessary for typescript
    conversion = {
      toBase: (v) => l`${v} * ${c}`,
      fromBase: (v) => l`${v} / ${c}`,
    };
  }
  const unit = normalizeUnit(makeUnit(unitSpec), [name as AtomicUnit]);
  const baseUnit = name === baseName ? unit : getUnitBang(baseName);

  const entry = registryGet(baseUnit);
  if (entry && !entry.isBaseUnit) {
    throw new Error(
      `Base unit ${baseName} for ${name} is not a base unit, but a derived unit. ` +
        'defCustomUnit only works with base units.'
    );
  }

  return defUnit(
    name as AtomicUnit,
    unit,
    false,
    false,
    new Map([[baseUnit, conversion]])
  );
}

/*
 * Returns a unit map from a string or object representation.  If using the object form
 * it does not perform any validation & will return exatly what you give it.
 */
export function makeUnit(unitSpec: UnitSpec): Unit {
  if (typeof unitSpec === 'string') {
    return parseUnitString(unitSpec);
  } else {
    return new Map(Object.entries(unitSpec)) as Map<AtomicUnit, number>;
  }
}

/*
 * Returns a list of all atomic unit names.
 */
export const atomicUnitNames: () => Array<string> = memoize(() => {
  return Array.from(registry.values()).map((entry) => entry.canonicalName);
});

/*
 * Returns a list of all unit names, including aliases.
 */
export const allUnitNames: () => Array<string> = memoize(() => {
  return Array.from(names.keys());
});

/*
 * Returns a unit map that is the same as the input, but with all atomic units
 * with non-zero exponents replaced by their canonical names (e.g. kg ->
 * kilogram) and all atomic units with zero exponents removed.
 *
 * If assumeValid is provided, it should be a list of atomic units that are
 * assumed to be valid and will not be checked against the registry.  This is used
 * for defining new units, where the unit being defined is not yet in the registry.
 */
export function normalizeUnit(
  u: Unit,
  assumeValid: Array<AtomicUnit> = [],
  { preserveName = false }: { preserveName?: boolean } = {}
): Unit {
  const out = new Map();
  for (const [atomicUnit, exponent] of u) {
    if (exponent !== 0) {
      if (!assumeValid.includes(atomicUnit)) {
        const unit = getUnitBang(atomicUnit);
        const entry = registryGet(unit);
        if (!entry) {
          throw new UnknownUnitError(atomicUnit);
        }
        out.set(preserveName ? atomicUnit : entry.canonicalName, exponent);
      } else {
        out.set(atomicUnit, exponent);
      }
    }
  }
  return out;
}

/*
 * Returns the canonical string representation of a unit.  The result will always
 * be something that makes sense to pass to parseUnitString().  Special names will
 * be used for common units ("newton" for "kg-m/s^2")
 */
export function unitToString(u: Unit): string {
  const entry = registryGet(u);
  if (entry) {
    return entry.canonicalName;
  }

  const numerators: Array<string> = [];
  const denominators: Array<string> = [];

  u.forEach((exponent, unit) => {
    if (exponent === 1) {
      numerators.push(unit);
    } else if (exponent > 1) {
      numerators.push(unit + '^' + exponent);
    } else if (exponent < -1) {
      denominators.push(unit + '^' + -exponent);
    } else {
      denominators.push(unit);
    }
  });

  const numeratorString = numerators.join('-');
  const denominatorString = denominators.join('-');
  return (
    numeratorString + (denominatorString !== '' ? '/' + denominatorString : '')
  );
}

export function isSameUnitString(
  s1: string | null | undefined,
  s2: string | null | undefined
): boolean {
  // This early return is here in case we see anything that isn't a proper Unit
  // but it's actually the same
  if (s1 === s2) {
    return true;
  }
  if (isNullish(s1) || isNullish(s2)) {
    return false;
  }
  const u1 = getUnit(s1);
  if (u1 === undefined) {
    return false;
  }
  const allStrings = unitToAllStrings(u1);
  return allStrings.includes(s2 as AtomicUnit);
}

/**
 * Return list of unit name and all aliases
 */
export function unitToAllStrings(u: Unit): Array<AtomicUnit> {
  const units = unitAliases.get(u) ?? [];
  units.push(unitToString(u) as AtomicUnit);
  return units;
}

/**
 * For all units in a group, returns a flattened list of units and all aliases
 */
export function unitGroupToAllStrings(
  unitGroup: Array<Unit>
): Array<AtomicUnit> {
  return Array.from(unitGroup.values()).flatMap((u) => unitToAllStrings(u));
}

/*
 * Returns a string representation of a unit map.  This is intended for debugging
 * and internal use only.
 */
function inspectUnit(u: Unit): string {
  return JSON.stringify(Object.fromEntries(u.entries()));
}

// Join two expressions together
function maybeComposeConversions(
  unit: Unit,
  baseUnit: Unit,
  conversion: Conversion
): [Unit, Conversion] {
  if (unit !== baseUnit) {
    const entry = must(registryGet(baseUnit));
    if (entry.isBaseUnit) {
      return [baseUnit, conversion];
    }
    invariant(
      entry.conversions.size === 1,
      "intermediate unit has multiple conversions; can't use"
    );
    const [realBaseUnit, baseConversion] = entry.conversions
      .entries()
      .next().value;

    return [
      realBaseUnit,
      {
        toBase: (v) => conversion.toBase(baseConversion.toBase(v)),
        fromBase: (v) => baseConversion.fromBase(conversion.fromBase(v)),
      },
    ];
  }
  return [baseUnit, conversion];
}

/*
 * This is for testing.  Do not use it in production code.
 */
export function testOnlyRemoveEntries(units: Array<Unit | string>): void {
  for (const unit of units) {
    if (typeof unit === 'string') {
      names.delete(unit as AtomicUnit);
    } else {
      registry.delete(inspectUnit(unit));
    }
  }
}

require('./definitions/all');
